import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import chatService from '../services/chatService';
import { useAuth } from '../hooks/useAuth';
import { MessageCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { getAccessToken, refreshToken } from '../services/authService';

interface ChatButtonProps {
  userId: string;
  className?: string;
  buttonText?: string;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  showIcon?: boolean;
}

const ChatButton: React.FC<ChatButtonProps> = ({ 
  userId, 
  className = '',
  buttonText = 'Message',
  variant = 'default',
  size = 'default',
  showIcon = true
}) => {
  const navigate = useNavigate();
  const { refresh } = useAuth(); // We only need the refresh function
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling to parent elements
    e.preventDefault(); // Prevent default behavior
    
    setIsLoading(true);
    
    try {
      // Fast path: If token exists, proceed immediately
      let token = getAccessToken();
      
      // Only refresh if no token found
      if (!token) {
        console.log("ChatButton: No token found, attempting refresh");
        try {
          token = await refreshToken();
          if (token) {
            console.log("ChatButton: Token refreshed successfully");
            await refresh(); // Update auth state
          } else {
            console.log("ChatButton: Token refresh failed");
            toast.error('Please sign in to send messages');
            setIsLoading(false);
            navigate('/signin', { replace: true });
            return;
          }
        } catch (error) {
          console.error("ChatButton: Token refresh error:", error);
          toast.error('Please sign in to send messages');
          setIsLoading(false);
          navigate('/signin', { replace: true });
          return;
        }
      }
      
      // Ensure we have a token now
      if (!token) {
        toast.error('Please sign in to send messages');
        setIsLoading(false);
        navigate('/signin', { replace: true });
        return;
      }
      
      // Fast path: Try to create/get chat room right away
      console.log('ChatButton: Creating chat room with user:', userId);
      
      try {
        const chatRoom = await chatService.createOrGetChatRoom(userId);
        console.log('ChatButton: Chat room created:', chatRoom);
        
        // Initialize WebSocket in the background after navigation
        // This allows us to navigate faster
        navigate(`/chat/${chatRoom.id}`, { replace: true });
        
        // Initialize WebSocket after navigation
        setTimeout(() => {
          chatService.initializeWebSocket(token as string);
        }, 500);
      } catch (error) {
        console.error('Failed to create chat room:', error);
        toast.error('Failed to open chat. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Failed to open chat:', error);
      toast.error('Failed to open chat. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      className={className}
      variant={variant}
      size={size}
      disabled={isLoading}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        showIcon && <MessageCircle className="h-4 w-4 mr-2" />
      )}
      {isLoading ? 'Opening...' : buttonText}
    </Button>
  );
};

export default ChatButton;
